/** @jsx jsx **/
import { jsx, Box, Container, Text, Heading } from 'theme-ui'
import { FC } from 'react'
import Layout from '~/components/layout'
import Img from 'gatsby-image'
import { graphql, useStaticQuery } from 'gatsby'
import { useMediaQuery } from 'react-responsive'
import GridPhotoWithListItem from '~/components/grid-photo-with-list-item'
import theme from '~/gatsby-plugin-theme-ui'
import { GatsbyImageType } from '~/@types/models'
import HeadingV2 from '~/componentsV2/heading'

type Props = {}

type AboutPageType = {
  pageData: {
    frontmatter: {
      header: {
        badge: string
        description: string
        image: GatsbyImageType
        title: string
      }
      textSection: string
      quote: {
        author: string
        content: string
      }
      textSection2: string
      ourPromise: {
        title: string
        sections: Array<{
          list: Array<{
            description: string
            title: string
          }>
          image1: GatsbyImageType
          image2: GatsbyImageType
        }>
      }
    }
  }
}

const AboutPage: FC<Props> = () => {
  const query = useStaticQuery<AboutPageType>(graphql`
    query {
      pageData: markdownRemark(frontmatter: { templateKey: { eq: "about" } }) {
        frontmatter {
          headerVariant
          header {
            badge
            description
            image {
              ...heroImage
            }
            title
          }
          textSection
          quote {
            author
            content
          }
          textSection2
          ourPromise {
            title
            sections {
              list {
                description
                title
              }
              image1 {
                ...aboutPageImage
              }
              image2 {
                ...aboutPageImage
              }
            }
          }
        }
      }
    }
  `)
  const { header, textSection, textSection2, ourPromise, quote } = query.pageData.frontmatter

  const isMobile = useMediaQuery({
    query: `(max-width: ${theme.breakpoints[2]})`,
  })
  return (
    <Layout headerVariant={isMobile ? 'light' : 'default'} title={header.title}>
      <Container
        as="section"
        sx={{
          position: 'relative',
          px: [0, null, null, theme.space.gridGap2],
        }}
      >
        <Img
          fluid={header.image.childImageSharp.fluid}
          sx={{
            maxWidth: 1032,
            height: ['100vh', 'auto'],
            ml: 'auto',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            width: ['auto', null, null, 726],
            bg: [null, null, null, 'main'],
            py: [null, null, null, theme.space.md3],
            ml: [theme.space.l, null, null, 155],
            mr: [theme.space.l, null, null, 0],
            mb: [theme.space.l, null, null, 155],
          }}
        >
          <Text
            variant="label"
            sx={{
              mb: '20px',
              color: ['main', null, null, 'muted'],
            }}
          >
            {header.badge}
          </Text>
          <HeadingV2
            sx={{
              mb: 29,
              color: ['main', null, null, 'text'],
            }}
          >
            {header.title}
          </HeadingV2>
          {!isMobile && (
            <Text
              variant="regular"
              sx={{
                maxWidth: 600,
              }}
            >
              {header.description}
            </Text>
          )}
        </Box>
      </Container>
      <Container
        variant="containerPrimary"
        sx={{
          my: [theme.space.l, null, theme.space.xl3],
        }}
      >
        {isMobile && (
          <Text variant="regular" mb={theme.space.md2}>
            {header.description}
          </Text>
        )}
        <Text variant="regular">{textSection}</Text>
        <Box
          as="blockquote"
          sx={{
            position: 'relative',
            my: [theme.space.l, null, theme.space.xl2],
            mx: 0,
            pl: theme.space.md3,
            ':before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              width: '2px',
              height: '100%',
              bg: 'text',
            },
          }}
        >
          <Heading
            as="div"
            variant="title5"
            sx={{
              lineHeight: ['36px', null, '48px'],
              mb: theme.space.gridGap3,
            }}
          >
            {quote.content}
          </Heading>
          <Text as="footer" variant="label">
            {quote.author}
          </Text>
        </Box>
        <Text variant="regular">{textSection2}</Text>
      </Container>
      <Container>
        <Heading variant="title3" mb={[theme.space.l1, null, theme.space.xl2]}>
          {ourPromise.title}
        </Heading>

        <Box>
          {ourPromise.sections &&
            ourPromise.sections.map(item => (
              <GridPhotoWithListItem
                key={item.list[0].title}
                image1={item.image1.childImageSharp.fluid}
                image2={item.image2.childImageSharp.fluid}
                list={item.list}
              />
            ))}
        </Box>
      </Container>
    </Layout>
  )
}

export default AboutPage
